import * as React from 'react'
import { Image, ScrollView, Text, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Button from '@components/button/Button'
import Tag from '@components/tag/Tag'
import Animators from '@components/root/Animators'

import useI18n from '@i18n/useI18n'

import { IncidentFormScheme } from '../scheme'

const ExhibitorLayout = ({
  step,
  form,
  onNext,
  onBack,
  onQuit,
  children,
  last,
  full,
  validation = () => true,
  fixed,
  data,
  loading,
}: IncidentLayoutProps) => {
  const i18n = useI18n()

  const [valid, setValid] = React.useState(false)

  React.useEffect(() => {
    const schema = IncidentFormScheme[step as keyof typeof IncidentFormScheme]

    if (schema) {
      schema
        .validate(form)
        .then(() => setValid(validation(form)))
        .catch(() => setValid(false))
    }
  }, [form, step, data])

  return (
    <MainContainer>
      <DualLine>
        <Column left>
          <Logo source={require('@assets/logo_viparis.png').default} resizeMode="contain" />

          <TagContainer>
            <Tag
              activeColor
              focused
              item={{ label: i18n.t('screens.incident.quit'), value: 'tag' }}
              selected={false}
              leftIcon="close"
              onItemSelected={onQuit}
            />
          </TagContainer>

          <Animators>
            <SubTitle>{i18n.t('screens.incident.START.title')}</SubTitle>
            <Title>{i18n.t(`screens.incident.${step}.title`, form)}</Title>
          </Animators>
        </Column>

        <Column full={full}>
          {fixed ? (
            children
          ) : (
            <ScrollView
              scrollIndicatorInsets={{ right: 1 }}
              contentContainerStyle={{ flexGrow: 1 }}
              bounces={false}
              keyboardShouldPersistTaps="handled">
              {children}
            </ScrollView>
          )}
        </Column>
      </DualLine>

      <Buttons>
        <ButtonContainer>
          <Button
            label={i18n.t('screens.incident.previous')}
            status="active"
            onClick={onBack}
            textOnly
            color="light"
            textColor={theme.colors.primaryText}
          />
        </ButtonContainer>
        <ButtonContainer>
          <Button
            label={i18n.t(`screens.incident.${last ? 'declare' : 'next'}`)}
            status={loading ? 'loading' : valid ? 'active' : 'disabled'}
            onClick={onNext}
          />
        </ButtonContainer>
      </Buttons>
    </MainContainer>
  )
}

export default ExhibitorLayout

const MainContainer = styled(View)`
  flex: 1;
  flex-direction: column;
`

const DualLine = styled(View)`
  flex: 1;
  flex-direction: row;
`

const Column = styled(View)<{ left?: boolean; full?: boolean }>`
  flex: 1;
  flex-direction: column;
  background-color: ${(props) => (props.left ? props.theme.colors.primary : props.theme.colors.backgroundSecondary)};
  padding: ${(props) => (props.full ? 0 : 25)}px;
  position: relative;
`

const SubTitle = styled(Text)`
  ${(props) => props.theme.fonts.Title2};
  color: ${(props) => props.theme.colors.background};
  font-size: 20px;
  margin: 25px;
  margin-top: 190px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Heading1};
  color: ${(props) => props.theme.colors.background};
  margin: 0 25px;
`

const Buttons = styled(View)`
  flex-direction: row;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)`
  width: 275px;
  margin: 12px;
`

const TagContainer = styled(View)`
  position: absolute;
  top: 40px;
  right: 30px;
`

const Logo = styled(Image)`
  position: absolute;
  top: 40px;
  left: 50px;
  width: 150px;
  height: 40px;
`
